import React, { MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo } from 'react'
import {
    Date as DateLanguage,
    DateTime as DateTimeLanguage,
    DaysOfTheWeek,
    DaysOfTheWeek as DaysOfTheWeekLanguage
} from '../../../../../i18n'
import { ReportTypes } from '../../../../NonReact/constants/ReportTypes'
// Utilities imports.
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { HelpTagTitles, Tags } from '../../../Common/API/Contentful/Constants'
import DateTimePicker, { DateTimePickerType, DateTimePickerVariant } from '../../../Common/Components/DateTimePicker'
import Heading from '../../../Common/Components/Heading'
import { HeadingLevel } from '../../../Common/Components/Heading/Heading'
import { HelpTag } from '../../../Common/Components/HelpTag/HelpTag'
// Commons imports.
import RadioButton, { RadioButtonProps } from '../../../Common/Components/RadioButton'
import { SelectMulti, SelectOptions } from '../../../Common/Components/Selects'
import TextInput from '../../../Common/Components/TextInput'
// Contexts imports.
import { LanguageContext } from '../../../Common/Contexts'
import ConfigureReportSettingsContext, {
    ConfigureReportSettingsContextType
} from '../../../Common/Contexts/ConfigureReportSettingsContext'
import { ReportDefinition } from '../../Types'
// Sub imports.
import {
    REPORT_DURATION_CUSTOM_DATETIME,
    REPORT_DURATION_DAY_CURRENT,
    REPORT_DURATION_DAY_PREVIOUS,
    REPORT_DURATION_LAST_X_DAYS,
    REPORT_DURATION_MONTH_CURRENT,
    REPORT_DURATION_MONTH_PREVIOUS,
    REPORT_DURATION_REALTIME,
    REPORT_DURATION_WEEK_CURRENT,
    REPORT_DURATION_WEEK_PREVIOUS
} from './Constants'
import { UserInfo, useUserInfo } from '../../../../ReactRouting/Hooks/useUserInfo'
import { ReportingAccessLevels } from '../../../AddUser/Reporting/ReportingAccessLevels'

// CSS imports. Commented out because of current build process.
// import './dateTimeSection.scss'

const MAX_DAYS_FOR_LAST_X: number = 365
const MIN_DAYS_FOR_LAST_X: number = 1

/**
 * Component properties type.
 */
type Props = {
    isPersonalDashboard: MutableRefObject<boolean>
    dateTimeOptionSelected: number
    setDateTimeOptionSelected: (value: number) => void
    selectedReportStyle: ReportDefinition | undefined
    reportTimeRangeStart: Date
    setReportTimeRangeStart: (value: Date) => void
    reportTimeRangeEnd: Date
    setReportTimeRangeEnd: (value: Date) => void
    reportDateTimeRangeStart: Date
    setReportDateTimeRangeStart: (value: Date) => void
    reportDateTimeRangeEnd: Date
    setReportDateTimeRangeEnd: (value: Date) => void
    reportDateTimeDaysOfTheWeek: string[]
    setReportDateTimeDaysOfTheWeek: (value: string[]) => void
    reportDateTimeLastXDays: number | string
    setReportDateTimeLastXDays: (value: number | string) => void
}

/**
 * Date / Time section in report tab.
 *
 * @param dateTimeOptionSelected Selected option for the date time field.
 * @param setDateTimeOptionSelected Setter for the date time option.
 * @param selectedReportStyle The currently selected report definition.
 * @param reportTimeRangeStart Start of report time range.
 * @param setReportTimeRangeStart Setter for property.
 * @param reportTimeRangeEnd End of report time range.
 * @param setReportTimeRangeEnd Setter for the property.
 * @param reportDateTimeRangeStart Start of report date/time range.
 * @param setReportDateTimeRangeStart Setter for the property.
 * @param reportDateTimeRangeEnd End of report date/time range.
 * @param setReportDateTimeRangeEnd Setter for the property.
 * @param reportDateTimeDaysOfTheWeek Days of the week for report duration.
 * @param reportDateTimeDaysOfTheWeek Setter for the property.
 * @param isCXAAdvancedUser Is the user an advanced user.
 */
const DateTimeSection: React.FC<Props> = ({
    isPersonalDashboard,
    dateTimeOptionSelected,
    setDateTimeOptionSelected,
    selectedReportStyle,
    reportTimeRangeStart,
    setReportTimeRangeStart,
    reportTimeRangeEnd,
    setReportTimeRangeEnd,
    reportDateTimeRangeStart,
    setReportDateTimeRangeStart,
    reportDateTimeRangeEnd,
    setReportDateTimeRangeEnd,
    reportDateTimeDaysOfTheWeek,
    setReportDateTimeDaysOfTheWeek,
    reportDateTimeLastXDays,
    setReportDateTimeLastXDays
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(DaysOfTheWeek.NAME)
    language.loadBundle(DateTimeLanguage.NAME)

    const [userInfo]: UserInfo = useUserInfo()

    const { isDateTimeOnly }: ConfigureReportSettingsContextType = useContext(ConfigureReportSettingsContext)

    useEffect(() => {
        if (isDateTimeOnly) {
            setDateTimeOptionSelected(REPORT_DURATION_CUSTOM_DATETIME)
        }
    }, [isDateTimeOnly])

    const customDateTimeOptionSelected: boolean = useMemo(
        (): boolean => dateTimeOptionSelected === REPORT_DURATION_CUSTOM_DATETIME,
        [dateTimeOptionSelected]
    )
    const lastXDaysSelected: boolean = useMemo(
        (): boolean => dateTimeOptionSelected === REPORT_DURATION_LAST_X_DAYS,
        [dateTimeOptionSelected]
    )
    const dailyOptionSelected: boolean = useMemo(
        (): boolean =>
            dateTimeOptionSelected === REPORT_DURATION_REALTIME ||
            dateTimeOptionSelected === REPORT_DURATION_DAY_CURRENT ||
            dateTimeOptionSelected === REPORT_DURATION_DAY_PREVIOUS ||
            lastXDaysSelected,
        [dateTimeOptionSelected, lastXDaysSelected]
    )
    const weeklyOptionSelected: boolean = useMemo(
        (): boolean =>
            dateTimeOptionSelected === REPORT_DURATION_WEEK_CURRENT ||
            dateTimeOptionSelected === REPORT_DURATION_WEEK_PREVIOUS ||
            lastXDaysSelected,
        [dateTimeOptionSelected, lastXDaysSelected]
    )
    const monthlyOptionSelected: boolean = useMemo(
        (): boolean =>
            dateTimeOptionSelected === REPORT_DURATION_MONTH_CURRENT ||
            dateTimeOptionSelected === REPORT_DURATION_MONTH_PREVIOUS,
        [dateTimeOptionSelected, lastXDaysSelected]
    )
    const isReportTypeDailyOptionsDisabled: boolean = useMemo(
        (): boolean =>
            selectedReportStyle?.ReportType === ReportTypes.ACTIVE_CALL_LIST ||
            selectedReportStyle?.ReportType === ReportTypes.DASHBOARD ||
            selectedReportStyle?.ReportType === ReportTypes.EXTERNAL_CONTENT,
        [selectedReportStyle?.ReportType]
    )
    const isReportTypeWeeklyOptionsDisabled: boolean = useMemo(
        (): boolean =>
            selectedReportStyle?.ReportType === ReportTypes.ACTIVE_CALL_LIST ||
            selectedReportStyle?.ReportType === ReportTypes.DASHBOARD ||
            selectedReportStyle?.ReportType === ReportTypes.EXTERNAL_CONTENT,
        [selectedReportStyle?.ReportType]
    )
    const isReportTypeCustomOptionsDisabled: boolean = useMemo(
        (): boolean => selectedReportStyle?.ReportType === 110,
        [selectedReportStyle?.ReportType]
    )

    const isReportTypeIntervalAndEntity: boolean =
        selectedReportStyle?.ReportType === ReportTypes.CALLS_BY_INTERVAL_AND_ENTITY

    const dayOfTheWeekOptions: SelectOptions<string> = useMemo(
        (): SelectOptions<string> => [
            {
                label: `${language.get(DaysOfTheWeekLanguage.Monday)}`,
                value: `${language.get(DaysOfTheWeekLanguage.Mondayshort)}`
            },
            {
                label: `${language.get(DaysOfTheWeekLanguage.Tuesday)}`,
                value: `${language.get(DaysOfTheWeekLanguage.Tuesdayshort)}`
            },
            {
                label: `${language.get(DaysOfTheWeekLanguage.Wednesday)}`,
                value: `${language.get(DaysOfTheWeekLanguage.Wednesdayshort)}`
            },
            {
                label: `${language.get(DaysOfTheWeekLanguage.Thursday)}`,
                value: `${language.get(DaysOfTheWeekLanguage.Thursdayshort)}`
            },
            {
                label: `${language.get(DaysOfTheWeekLanguage.Friday)}`,
                value: `${language.get(DaysOfTheWeekLanguage.Fridayshort)}`
            },
            {
                label: `${language.get(DaysOfTheWeekLanguage.Saturday)}`,
                value: `${language.get(DaysOfTheWeekLanguage.Saturdayshort)}`
            },
            {
                label: `${language.get(DaysOfTheWeekLanguage.Sunday)}`,
                value: `${language.get(DaysOfTheWeekLanguage.Sundayshort)}`
            }
        ],
        []
    )

    const getDailyOptions = useCallback((): boolean => {
        let showDailyOptions = false

        if (dailyOptionSelected && !isReportTypeDailyOptionsDisabled) {
            showDailyOptions = true
        }

        return showDailyOptions
    }, [dailyOptionSelected, isReportTypeDailyOptionsDisabled])

    const getWeeklyOptions = useCallback((): boolean => {
        let showWeeklyOptions = false

        if (
            (weeklyOptionSelected || monthlyOptionSelected || customDateTimeOptionSelected) &&
            !isReportTypeWeeklyOptionsDisabled
        ) {
            showWeeklyOptions = true
        }

        return showWeeklyOptions
    }, [weeklyOptionSelected, monthlyOptionSelected, customDateTimeOptionSelected, isReportTypeWeeklyOptionsDisabled])

    const getCustomOptions = useCallback((): boolean => {
        let showCustomOptions = false

        if (customDateTimeOptionSelected && !isReportTypeCustomOptionsDisabled) {
            showCustomOptions = true
        }

        return showCustomOptions
    }, [customDateTimeOptionSelected, isReportTypeCustomOptionsDisabled])

    // Radio component value is casted to String by DOM API - we need to wrap parse this to number.
    const setDateTimeOptionSelectedString = useCallback((value: string): void => {
        setDateTimeOptionSelected(parseInt(value, 10))
    }, [])

    const nonPDOptions = useCallback((): RadioButtonProps[] => {
        if (isPersonalDashboard.current) {
            return []
        }

        return [
            {
                value: REPORT_DURATION_DAY_CURRENT,
                isChecked: dateTimeOptionSelected === REPORT_DURATION_DAY_CURRENT,
                labelRight: language.get(DateTimeLanguage.RadiolabelToday),
                onChange: setDateTimeOptionSelectedString
            },
            {
                value: REPORT_DURATION_DAY_PREVIOUS,
                isChecked: dateTimeOptionSelected === REPORT_DURATION_DAY_PREVIOUS,
                labelRight: language.get(DateTimeLanguage.RadiolabelYesterday),
                onChange: setDateTimeOptionSelectedString
            },
            {
                value: REPORT_DURATION_LAST_X_DAYS,
                isChecked: lastXDaysSelected,
                labelRight: language.get(DateTimeLanguage.RadiolabelLastXDays),
                onChange: setDateTimeOptionSelectedString
            },
            {
                value: REPORT_DURATION_WEEK_CURRENT,
                isChecked: dateTimeOptionSelected === REPORT_DURATION_WEEK_CURRENT,
                labelRight: language.get(DateTimeLanguage.RadiolabelThisweek),
                onChange: setDateTimeOptionSelectedString
            },
            {
                value: REPORT_DURATION_WEEK_PREVIOUS,
                isChecked: dateTimeOptionSelected === REPORT_DURATION_WEEK_PREVIOUS,
                labelRight: language.get(DateTimeLanguage.RadiolabelLastweek),
                onChange: setDateTimeOptionSelectedString
            },
            {
                value: REPORT_DURATION_MONTH_CURRENT,
                isChecked: dateTimeOptionSelected === REPORT_DURATION_MONTH_CURRENT,
                labelRight: language.get(DateTimeLanguage.RadiolabelThismonth),
                onChange: setDateTimeOptionSelectedString
            },
            {
                value: REPORT_DURATION_MONTH_PREVIOUS,
                isChecked: dateTimeOptionSelected === REPORT_DURATION_MONTH_PREVIOUS,
                labelRight: language.get(DateTimeLanguage.RadiolabelLastmonth),
                onChange: setDateTimeOptionSelectedString
            },
            {
                value: REPORT_DURATION_CUSTOM_DATETIME,
                isChecked: customDateTimeOptionSelected,
                labelRight: language.get(DateTimeLanguage.RadiolabelCustom),
                onChange: setDateTimeOptionSelectedString
            }
        ]
    }, [dateTimeOptionSelected, customDateTimeOptionSelected, lastXDaysSelected])

    // Date time radio button items properties.
    const dateTimeRadioButtonItems: RadioButtonProps[] = useMemo(
        () => [
            {
                value: REPORT_DURATION_REALTIME,
                isChecked: dateTimeOptionSelected === REPORT_DURATION_REALTIME,
                labelRight: language.get(DateTimeLanguage.RadiolabelRealtime),
                onChange: setDateTimeOptionSelectedString,
                disabled:
                    userInfo?.Scope?.ReportingAccess === ReportingAccessLevels.CXA_STANDARD ||
                    isReportTypeIntervalAndEntity
            },
            ...nonPDOptions()
        ],
        [
            dateTimeOptionSelected,
            customDateTimeOptionSelected,
            lastXDaysSelected,
            userInfo?.Scope?.ReportingAccess,
            isReportTypeIntervalAndEntity
        ]
    )

    /**
     * Builds radio button date time option element.
     *
     * @param radioButtonItem The radio button properties.
     */
    const ToDateTimeRadioButton = (radioButtonItem: RadioButtonProps): ReactElement => {
        const { value, isChecked, labelRight, onChange } = radioButtonItem

        return (
            <RadioButton
                key={`${value}`}
                value={value}
                isChecked={isChecked}
                labelRight={labelRight}
                onChange={onChange}
            />
        )
    }

    return (
        <div className='date-time-section'>
            <div className='heading1 date-time'>
                {language.get(DateTimeLanguage.SectionheaderDatetime)}
                <HelpTag
                    className='configure-report-help-title-tag'
                    helpContentTitle={HelpTagTitles.CONFIGURE_REPORT_DATE_TIME}
                    tag={Tags.CONFIGURE_REPORT}
                />
            </div>
            <div className='date-time-section-content'>
                <div className='options-panel'>
                    {dateTimeRadioButtonItems
                        .filter((btn: RadioButtonProps) => !btn.disabled)
                        .map(ToDateTimeRadioButton)}
                </div>
                <div className='custom-option-settings'>
                    {getCustomOptions() && (
                        <div className='setting date-time-range'>
                            <div className='header'>{language.get(DateTimeLanguage.SectionheaderDaterange)}</div>
                            <div className='inputs'>
                                <DateTimePicker
                                    type={DateTimePickerType.DATETIME}
                                    variant={DateTimePickerVariant.INLINE}
                                    value={reportDateTimeRangeStart}
                                    handleChange={setReportDateTimeRangeStart}
                                    dateTimeViews={['date', 'month', 'year', 'hours', 'minutes', 'seconds']}
                                    format={`${language.get(DateLanguage.DatePattern)} HH:mm:ss`}
                                    labelLeft={language.get(DateTimeLanguage.InputlabelBetween)}
                                    isAMPMDisplayed={false}
                                />
                                <DateTimePicker
                                    type={DateTimePickerType.DATETIME}
                                    variant={DateTimePickerVariant.INLINE}
                                    value={reportDateTimeRangeEnd}
                                    handleChange={setReportDateTimeRangeEnd}
                                    dateTimeViews={['date', 'month', 'year', 'hours', 'minutes', 'seconds']}
                                    format={`${language.get(DateLanguage.DatePattern)} HH:mm:ss`}
                                    labelLeft={language.get(DateTimeLanguage.InputlabelAnd)}
                                    isAMPMDisplayed={false}
                                />
                            </div>
                        </div>
                    )}

                    {lastXDaysSelected ? (
                        <div className='setting last-x-days'>
                            <div className='header'>{language.get(DateTimeLanguage.SectionHeaderLastXDays)}</div>
                            <div className='inputs'>
                                <TextInput
                                    labelLeft={language.get(DateTimeLanguage.RadiolabelDays)}
                                    value={reportDateTimeLastXDays}
                                    onChange={(v: string): void => {
                                        if (IsStringEmpty(v)) {
                                            setReportDateTimeLastXDays('')
                                        }

                                        const valueAsNumber: number = parseInt(v, 10)

                                        if (Number.isNaN(valueAsNumber)) {
                                            return undefined
                                        }
                                        if (valueAsNumber > MAX_DAYS_FOR_LAST_X) {
                                            setReportDateTimeLastXDays(MAX_DAYS_FOR_LAST_X)
                                        } else if (valueAsNumber < MIN_DAYS_FOR_LAST_X) {
                                            setReportDateTimeLastXDays(MIN_DAYS_FOR_LAST_X)
                                        } else {
                                            setReportDateTimeLastXDays(valueAsNumber)
                                        }

                                        return undefined
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <></>
                    )}

                    {getWeeklyOptions() && (
                        <div className='setting days-of-the-week'>
                            <div className='header'>{language.get(DateTimeLanguage.SectionheaderDaysoftheweek)}</div>
                            <div className='inputs'>
                                <SelectMulti
                                    label={language.get(DateTimeLanguage.RadiolabelDays)}
                                    options={dayOfTheWeekOptions}
                                    value={reportDateTimeDaysOfTheWeek}
                                    handleChange={setReportDateTimeDaysOfTheWeek}
                                    renderSelectedValue={true}
                                />
                            </div>
                        </div>
                    )}

                    {(getDailyOptions() || getWeeklyOptions()) && (
                        <div className='setting between'>
                            <Heading level={HeadingLevel.ONE}>
                                {language.get(DateTimeLanguage.SectionheaderTimerange)}
                            </Heading>
                            <div className='inputs'>
                                <DateTimePicker
                                    timeViews={['hours', 'minutes', 'seconds']}
                                    type={DateTimePickerType.TIME}
                                    variant={DateTimePickerVariant.INLINE}
                                    value={reportTimeRangeStart}
                                    handleChange={setReportTimeRangeStart}
                                    format={'HH:mm:ss'}
                                    labelLeft={language.get(DateTimeLanguage.InputlabelBetween)}
                                    isAMPMDisplayed={false}
                                />
                                <DateTimePicker
                                    timeViews={['hours', 'minutes', 'seconds']}
                                    type={DateTimePickerType.TIME}
                                    variant={DateTimePickerVariant.INLINE}
                                    value={reportTimeRangeEnd}
                                    handleChange={setReportTimeRangeEnd}
                                    format={'HH:mm:ss'}
                                    labelLeft={language.get(DateTimeLanguage.InputlabelAnd)}
                                    isAMPMDisplayed={false}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default DateTimeSection
